import React, { useEffect, useState } from "react";
import { Plus, Save, Trash2, X, Upload, RefreshCcw, GraduationCap } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { resolveImageUrl } from "../../lib/imageUrl";

const empty = () => ({
    name: "Yeni Hoca",
    title: "Rehber",
    bio: "",
    photo: "",
    languages: ["Türkçe"],
    experience_years: 0,
    order: 0,
    active: true,
});

const AdminGuides = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/guides");
            setItems(data);
        } catch { toast.error("Rehberler yüklenemedi"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const update = (i, field, value) => {
        const n = [...items];
        if (["experience_years", "order"].includes(field)) value = Number(value) || 0;
        n[i][field] = value;
        setItems(n);
    };

    const addNew = async () => {
        try {
            const { data } = await api.post("/admin/guides", empty());
            setItems([data, ...items]);
            toast.success("Yeni rehber eklendi");
        } catch { toast.error("Eklenemedi"); }
    };

    const save = async (i) => {
        const g = items[i];
        try {
            await api.put(`/admin/guides/${g.id}`, g);
            toast.success("Kaydedildi");
        } catch { toast.error("Kaydedilemedi"); }
    };

    const remove = async (i) => {
        const g = items[i];
        if (!window.confirm(`${g.name} silinsin mi?`)) return;
        try {
            await api.delete(`/admin/guides/${g.id}`);
            setItems(items.filter((_, idx) => idx !== i));
            toast.success("Silindi");
        } catch { toast.error("Silinemedi"); }
    };

    const uploadPhoto = async (i, file) => {
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const n = [...items];
            n[i].photo = data.url;
            setItems(n);
            toast.success("Fotoğraf yüklendi — kaydet'i unutmayın");
        } catch { toast.error("Fotoğraf yüklenemedi"); }
    };

    const addLang = (i, value) => {
        const v = String(value || "").trim();
        if (!v) return;
        const n = [...items];
        n[i].languages = [...(n[i].languages || []), v];
        setItems(n);
    };
    const removeLang = (i, li) => {
        const n = [...items];
        n[i].languages.splice(li, 1);
        setItems(n);
    };

    return (
        <div>
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                <div>
                    <h1 className="font-serif text-4xl mb-2">Rehberlik Hizmetimiz</h1>
                    <p className="text-muted_green text-sm">Gruplara rehberlik yapacak profesyonel hocalarımızın kadrosunu buradan yönetebilirsiniz.</p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={load} className="btn-outline-gold !text-xs" data-testid="refresh-guides"><RefreshCcw className="w-3.5 h-3.5" /> Yenile</button>
                    <button onClick={addNew} className="btn-gold !text-xs" data-testid="add-guide"><Plus className="w-3.5 h-3.5" /> Yeni Hoca</button>
                </div>
            </div>

            {loading ? (
                <div className="text-muted_green text-sm">Yükleniyor…</div>
            ) : items.length === 0 ? (
                <div className="border border-dashed border-border_green p-12 text-center">
                    <GraduationCap className="w-14 h-14 text-muted_green mx-auto mb-3" />
                    <div className="text-muted_green text-sm">Henüz hoca eklenmemiş. &ldquo;Yeni Hoca&rdquo; ile başlayın.</div>
                </div>
            ) : (
                <div className="space-y-6">
                    {items.map((g, i) => (
                        <div key={g.id} className="border border-border_green bg-emerald_surface p-5" data-testid={`admin-guide-${g.id}`}>
                            <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
                                <input className="input-dark text-lg font-serif flex-1 min-w-[200px]" value={g.name} onChange={(e) => update(i, "name", e.target.value)} placeholder="Hoca adı" data-testid={`name-${g.id}`} />
                                <label className="flex items-center gap-2 text-xs">
                                    <input type="checkbox" checked={!!g.active} onChange={(e) => update(i, "active", e.target.checked)} />
                                    <span className="text-muted_green">Aktif</span>
                                </label>
                                <button onClick={() => save(i)} className="btn-gold !text-xs" data-testid={`save-${g.id}`}><Save className="w-3.5 h-3.5" /> Kaydet</button>
                                <button onClick={() => remove(i)} className="text-rose-400 hover:text-rose-500 p-2" data-testid={`del-${g.id}`}><Trash2 className="w-4 h-4" /></button>
                            </div>

                            <div className="grid md:grid-cols-3 gap-5">
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.18em] text-gold mb-1">Fotoğraf</div>
                                    <div className="aspect-[4/5] bg-emerald_deep border border-border_green overflow-hidden flex items-center justify-center mb-2">
                                        {g.photo ? (
                                            <img src={resolveImageUrl(g.photo)} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <GraduationCap className="w-14 h-14 text-muted_green" />
                                        )}
                                    </div>
                                    <label className="btn-outline-gold !text-xs w-full justify-center cursor-pointer">
                                        <Upload className="w-3.5 h-3.5" /> Fotoğraf Yükle
                                        <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(i, e.target.files?.[0])} data-testid={`upload-${g.id}`} />
                                    </label>
                                </div>

                                <div className="md:col-span-2 space-y-3">
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="label-dark">Ünvan</label>
                                            <input className="input-dark" value={g.title} onChange={(e) => update(i, "title", e.target.value)} placeholder="Baş Rehber / İlahiyat Mezunu" />
                                        </div>
                                        <div>
                                            <label className="label-dark">Tecrübe (yıl)</label>
                                            <input type="number" className="input-dark" value={g.experience_years} onChange={(e) => update(i, "experience_years", e.target.value)} />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="label-dark">Özgeçmiş / Tanıtım</label>
                                        <textarea rows={4} className="input-dark" value={g.bio} onChange={(e) => update(i, "bio", e.target.value)} placeholder="İlmi geçmişi, tecrübe alanları, umre/hac tecrübeleri..." />
                                    </div>
                                    <div>
                                        <label className="label-dark">Diller</label>
                                        <div className="flex flex-wrap gap-1.5 mb-2">
                                            {(g.languages || []).map((l, li) => (
                                                <span key={li} className="bg-emerald_deep/60 border border-border_green text-bone text-xs px-2 py-1 inline-flex items-center gap-1">
                                                    {l}
                                                    <button onClick={() => removeLang(i, li)} className="text-rose-400 hover:text-rose-500"><X className="w-3 h-3" /></button>
                                                </span>
                                            ))}
                                        </div>
                                        <input
                                            className="input-dark"
                                            placeholder="Dil ekle ve Enter (örn: Türkçe, Arapça, İngilizce)"
                                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLang(i, e.target.value); e.target.value = ""; } }}
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="label-dark">Sıra</label>
                                            <input type="number" className="input-dark" value={g.order} onChange={(e) => update(i, "order", e.target.value)} placeholder="Sıralama numarası (küçük önde)" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AdminGuides;
